// Loot & shops at the table (issue 1156): a DM-only tool in the Table menu with three tabs. Treasure rolls a hoard from
// the site's treasure generator (by challenge rating or by budget, at most so many items): its coins, gems and items
// with their prices. Shop stocks a shop from the site's shop generator — one of the site's shop kinds in a settlement
// size, or by budget when the system has no kinds. Saved shops lists the DM's saved shops on the site and opens one's
// wares. Every item that is an entry of the site's items has "Give to…", the compendium's own list (the characters
// whose tokens stand on the board the DM is looking at) and its own give (#1055); a given row is marked with whom it
// went to. All of it is read over HTTP for the DM alone: nothing is told to the table, and the player given a gift is
// told it in a notice of their own ("The DM gave you Rope.").
//
// qa-runs 1156 (the tabletop half): under a rolled hoard, and on each held hoard, the DM (or a co-GM with tokens) shares
// out the coins and gems — to one person, by percentage, evenly among the campaign's whole party, or holds the hoard
// under a name (`HoardShare`). Held hoards are kept with the campaign's table and listed here, their items still with
// "Give to…" (a given item leaves the hoard, so it cannot be given twice) and a Discard… that asks first; players
// never see one.
//
// Issue 1585 (slice 2): each result shown — a hoard, a shop's wares, a saved shop opened — is remembered in this
// browser for the scene the DM is looking at (`rememberLoot`), the Compendium's "Might find here".
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShell } from '../context';
import { useOptionalTableState } from '../../table/TableState';
import { discardHoard, giveHeldItem, itemKey, listHoards, onHoardsChanged, renameHoard, type HeldHoard, listSavedShops, lootStartWords, readLootStart, readSavedShop, readShopKinds, rollHoard, stockShop, type Hoard, type LootItem, type LootStart, type SavedShop, type SavedShopRow, type ShopKinds } from '../../loot';
import { GiveList } from './CompendiumPanel';
import { HoardShare, coinWords } from './HoardShare';
import { BTN } from '../ui';
import { ConfirmDialog } from '../Dialogs';
import { rememberLoot } from '../../compendiumHome';

type Tab = 'treasure' | 'shop' | 'saved';
export const LOOT_TABS: ReadonlyArray<{ id: Tab; label: string }> = [{ id: 'treasure', label: 'Treasure' }, { id: 'shop', label: 'Shop' }, { id: 'saved', label: 'Saved shops' }];
const DOWN = "The site's generators can't be reached just now. Try again in a moment.";
const btn = `${BTN} shrink-0`;
const field = 'w-20 rounded border border-stone-700 bg-stone-900 px-1 py-0.5 text-xs';
const said = (r: { kind: 'refused'; reason: string } | { kind: 'error' }) => (r.kind === 'refused' ? r.reason : DOWN);
const money = (n: number | null) => (n === null ? '' : n.toLocaleString());

export function LootPanel() {
  const { campaignId, table } = useShell();
  const tableState = useOptionalTableState();
  const canGive = table.me.role === 'dm' || !!tableState?.powers?.includes('tokens');
  const sceneId = tableState?.scene?.id ?? null;
  const [tab, setTab] = useState<Tab>('treasure');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Treasure
  const [by, setBy] = useState<'cr' | 'budget'>('cr');
  // Starts at the party's level, else the last number used here, else 1 (qa-runs#1157); the DM types over it.
  const [amount, setAmount] = useState('1');
  const [start, setStart] = useState<LootStart | null>(null);
  const typed = useRef(false);
  const [maxItems, setMaxItems] = useState('8');
  const [hoard, setHoard] = useState<Hoard | null>(null);
  // Shop
  const [kinds, setKinds] = useState<ShopKinds | null>(null);
  const [kind, setKind] = useState('');
  const [size, setSize] = useState('');
  const [shopBudget, setShopBudget] = useState('');
  const [wares, setWares] = useState<LootItem[] | null>(null);
  // Saved shops
  const [saved, setSaved] = useState<SavedShopRow[] | null>(null);
  const [open, setOpen] = useState<SavedShop | null>(null);
  // What was given from the list on show, and to whom; a new roll starts afresh.
  const [given, setGiven] = useState<Map<string, string[]>>(new Map());
  const [giving, setGiving] = useState<LootItem | null>(null);
  // Sharing out (qa-runs 1156): which hoard is being shared ('roll' or a held hoard's id), what the last share said,
  // whether the roll's coins and gems are dealt with, and the held hoards.
  const [sharing, setSharing] = useState<string | null>(null);
  const [shareSaid, setShareSaid] = useState('');
  const [rollShared, setRollShared] = useState(false);
  const [held, setHeld] = useState<HeldHoard[] | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  // The held hoard whose item is being given (its give goes through the hoard, which loses it), and the one to discard.
  const [givingFrom, setGivingFrom] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<HeldHoard | null>(null);

  const fresh = () => { setGiven(new Map()); setGiving(null); setGivingFrom(null); setError(''); };
  const loadKinds = useCallback(async () => {
    const got = await readShopKinds(campaignId).catch(() => ({ kind: 'error' as const }));
    if (got.kind === 'ok') {
      setKinds({ kinds: got.kinds, sizes: got.sizes });
      setKind((k) => k || got.kinds[0]?.slug || ''); setSize((s) => s || got.sizes[0]?.slug || '');
    } else { setKinds({ kinds: [], sizes: [] }); setError(said(got)); }
  }, [campaignId]);
  const loadSaved = useCallback(async () => {
    const got = await listSavedShops(campaignId).catch(() => ({ kind: 'error' as const }));
    if (got.kind === 'ok') setSaved(got.shops); else { setSaved([]); setError(said(got)); }
  }, [campaignId]);
  useEffect(() => {
    let live = true;
    void readLootStart(campaignId).catch(() => null).then((got) => {
      if (!live || !got) return;
      setStart(got);
      if (!typed.current) setAmount(String(got.cr));
    });
    return () => { live = false; };
  }, [campaignId]);
  // Read over the list on show, never blanked first: the rows stay mounted, so the scroll position, an open Give to…,
  // Give out… or Rename all survive it (qa-runs 1197). Only the latest read counts; a failed one keeps what is shown.
  const heldReads = useRef(0);
  const readHeld = useCallback(async () => {
    const n = ++heldReads.current;
    const got = await listHoards(campaignId).catch(() => ({ kind: 'error' as const }));
    if (n !== heldReads.current) return;
    setHeld((was) => (got.kind === 'ok' ? got.hoards : was ?? []));
  }, [campaignId]);
  const onTreasure = canGive && tab === 'treasure';
  useEffect(() => { if (onTreasure && held === null) void readHeld(); }, [onTreasure, held, readHeld]);
  // Another DM or co-GM view changed a held hoard (qa-runs 1197): the list is read again, now if it is on show.
  useEffect(() => onHoardsChanged((id) => {
    if (id !== campaignId) return;
    if (onTreasure) void readHeld(); else setHeld(null);
  }), [campaignId, onTreasure, readHeld]);
  useEffect(() => { if (tab === 'shop' && kinds === null) void loadKinds(); }, [tab, kinds, loadKinds]);
  useEffect(() => { if (tab === 'saved' && saved === null) void loadSaved(); }, [tab, saved, loadSaved]);

  const roll = async () => {
    fresh(); setBusy(true); setSharing(null); setShareSaid(''); setRollShared(false);
    const n = Number(maxItems);
    const got = await rollHoard(campaignId, { ...(by === 'cr' ? { cr: amount.trim() } : { budget: amount.trim() }), ...(Number.isInteger(n) && n > 0 ? { maxItems: n } : {}) }).catch(() => ({ kind: 'error' as const }));
    setBusy(false);
    if (got.kind === 'ok') { setHoard(got.hoard); rememberLoot(table.me.id, campaignId, sceneId, 'Treasure hoard', got.hoard.items); } else { setHoard(null); setError(said(got)); }
  };
  const stock = async () => {
    fresh(); setBusy(true);
    const hasKinds = !!kinds && (kinds.kinds.length > 0 || kinds.sizes.length > 0);
    const got = await stockShop(campaignId, hasKinds ? { ...(kind ? { kind } : {}), ...(size ? { size } : {}), ...(shopBudget.trim() ? { budget: shopBudget.trim() } : {}) } : { ...(shopBudget.trim() ? { budget: shopBudget.trim() } : {}) })
      .catch(() => ({ kind: 'error' as const }));
    setBusy(false);
    const shopName = hasKinds ? kinds!.kinds.find((k) => k.slug === kind)?.name : undefined;
    if (got.kind === 'ok') { setWares(got.wares); rememberLoot(table.me.id, campaignId, sceneId, shopName ? `Shop: ${shopName}` : 'Shop', got.wares); } else { setWares(null); setError(said(got)); }
  };
  const openShop = async (id: string) => {
    fresh(); setBusy(true);
    const got = await readSavedShop(campaignId, id).catch(() => ({ kind: 'error' as const }));
    setBusy(false);
    if (got.kind === 'ok') { setOpen(got.shop); rememberLoot(table.me.id, campaignId, sceneId, `Shop: ${got.shop.name}`, got.shop.wares); } else { setOpen(null); setError(said(got)); }
  };
  const shared = (o: { said: string; held: HeldHoard | null; heldBefore?: string }) => {
    setShareSaid(o.said); setSharing(null);
    if (!o.heldBefore) setRollShared(true);
    setHeld((was) => {
      const rest = (was ?? []).filter((h) => h.id !== o.heldBefore && h.id !== o.held?.id);
      return o.held ? [...rest, o.held] : rest;
    });
  };
  const rename = async () => {
    if (!renaming || !renaming.name.trim()) return;
    const got = await renameHoard(campaignId, renaming.id, renaming.name.trim()).catch(() => ({ kind: 'error' as const }));
    if (got.kind === 'ok') { setHeld((was) => (was ?? []).map((h) => (h.id === got.hoard.id ? got.hoard : h))); setRenaming(null); } else setError(said(got));
  };
  // A held hoard as the server now has it: replaced, or gone (null) when nothing is left in it.
  const heldNow = (id: string, h: HeldHoard | null) => setHeld((was) => (h ? (was ?? []).map((x) => (x.id === id ? h : x)) : (was ?? []).filter((x) => x.id !== id)));
  const discard = async (h: HeldHoard) => {
    setDiscarding(null);
    const got = await discardHoard(campaignId, h.id).catch(() => ({ kind: 'error' as const }));
    if (got.kind === 'ok') { heldNow(h.id, null); setShareSaid(got.said); if (sharing === h.id) setSharing(null); } else setError(said(got));
  };
  const sendHeld = (hoardId: string, item: LootItem) => async (characterId: string) => {
    const got = await giveHeldItem(campaignId, hoardId, { characterId, segment: item.segment, slug: item.slug, sceneId });
    if (got.kind !== 'ok') return got;
    heldNow(hoardId, got.held); setShareSaid(got.said); setGiving(null); setGivingFrom(null);
    return { kind: 'ok' as const, said: got.said };
  };
  const coinsOf = (h: Hoard) => Object.fromEntries(h.coins.filter((c) => c.amount > 0).map((c) => [c.coin, c.amount]));
  const markGiven = (item: LootItem, to: string) => setGiven((was) => new Map(was).set(itemKey(item), [...(was.get(itemKey(item)) ?? []), to]));

  const list = (rows: LootItem[], empty: string, testid: string, from: string | null = null) => (
    <Items rows={rows} empty={empty} testid={testid} canGive={canGive} given={from ? new Map() : given} giving={givingFrom === from ? giving : null} onGive={(i) => { setGiving(i); setGivingFrom(from); }}
      giveList={giving && givingFrom === from ? (from
        ? <GiveList hit={giving} kind="item" sceneId={sceneId} onClose={() => { setGiving(null); setGivingFrom(null); }} send={sendHeld(from, giving)} />
        : <GiveList hit={giving} kind="item" sceneId={sceneId} onClose={() => setGiving(null)} onGiven={(to) => markGiven(giving, to)} />) : null} />
  );

  return (
    <div className="flex h-full flex-col p-2 text-sm" data-testid="loot">
      <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Loot and shops">
        {LOOT_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} data-testid={`loot-tab-${t.id}`}
            className={`rounded px-2 py-0.5 text-xs ${tab === t.id ? 'bg-stone-700 text-stone-100' : 'text-stone-400 hover:bg-stone-800'}`}
            onClick={() => { setTab(t.id); fresh(); setSharing(null); }}>{t.label}</button>
        ))}
      </div>
      {error && <p role="alert" className="mt-1 text-xs text-amber-300" data-testid="loot-error">{error}</p>}
      <div className="mt-2 min-h-0 flex-1 overflow-auto" role="tabpanel">
        {tab === 'treasure' && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-stone-400">By
                <select className={`${field} ml-1 w-auto`} value={by} data-testid="loot-by" onChange={(e) => setBy(e.target.value as 'cr' | 'budget')}>
                  <option value="cr">Challenge rating</option>
                  <option value="budget">Budget</option>
                </select>
              </label>
              <label className="text-xs text-stone-400">{by === 'cr' ? 'CR' : 'Budget'}
                <input className={`${field} ml-1`} inputMode="decimal" value={amount} data-testid="loot-amount" aria-label={by === 'cr' ? 'Challenge rating' : 'Budget'} onChange={(e) => { typed.current = true; setAmount(e.target.value); }} />
                {by === 'cr' && start && amount.trim() === String(start.cr) && lootStartWords(start) && <span className="ml-1 text-stone-500" data-testid="loot-start">{lootStartWords(start)}</span>}
              </label>
              <label className="text-xs text-stone-400">Max items
                <input className={`${field} ml-1 w-14`} inputMode="numeric" value={maxItems} data-testid="loot-max-items" aria-label="Most items" onChange={(e) => setMaxItems(e.target.value)} />
              </label>
              <button type="button" className={btn} disabled={busy} data-testid="loot-roll" onClick={() => void roll()}>Roll a hoard</button>
            </div>
            {hoard && (
              <div data-testid="loot-hoard" className="space-y-1">
                {hoard.total !== null && <p className="text-xs text-stone-400">Worth {money(hoard.total)} in all.</p>}
                <p className="text-xs text-stone-300" data-testid="loot-coins">
                  {hoard.coins.length ? hoard.coins.map((c) => `${c.amount.toLocaleString()} ${c.coin}`).join(', ') : 'No loose coins.'}
                </p>
                {hoard.gems.length > 0 && <p className="text-xs text-stone-300" data-testid="loot-gems">Gems: {hoard.gems.map((g) => (g.value === null ? g.name : `${g.name} (${money(g.value)})`)).join(', ')}</p>}
                {list(hoard.items, 'No items: the hoard is all coins and gems.', 'loot-item')}
                {canGive && !rollShared && (hoard.coins.length > 0 || hoard.gems.length > 0) && (sharing === 'roll'
                  ? <HoardShare source={{ coins: coinsOf(hoard), gems: hoard.gems, items: hoard.items.filter((i) => !given.has(itemKey(i))) }} onDone={shared} />
                  : <button type="button" className={btn} data-testid="hoard-share-roll" onClick={() => { setSharing('roll'); setShareSaid(''); }}>Give out the coins and gems…</button>)}
              </div>
            )}
            {shareSaid && <p role="status" className="text-xs text-emerald-300" data-testid="hoard-said">{shareSaid}</p>}
            {canGive && held && held.length > 0 && (
              <div className="space-y-1 border-t border-stone-800 pt-2" data-testid="held-hoards">
                <p className="text-xs font-medium text-stone-300">Held hoards</p>
                {held.map((h) => (
                  <div key={h.id} className="rounded border border-stone-700 p-1" data-testid={`held-${h.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {renaming?.id === h.id ? (
                        <>
                          <input className={`${field} w-40 max-w-full`} value={renaming.name} maxLength={80} aria-label="The held hoard's name" data-testid={`held-name-${h.id}`} onChange={(e) => setRenaming({ id: h.id, name: e.target.value })} />
                          <button type="button" className={btn} data-testid={`held-save-${h.id}`} onClick={() => void rename()}>Save</button>
                        </>
                      ) : <span className="text-stone-100">{h.name}</span>}
                      {renaming?.id !== h.id && <button type="button" className={`${btn} ml-auto`} data-testid={`held-rename-${h.id}`} onClick={() => setRenaming({ id: h.id, name: h.name })}>Rename</button>}
                      {(Object.keys(h.coins).length > 0 || h.gems.length > 0) && sharing !== h.id && (
                        <button type="button" className={btn} data-testid={`held-share-${h.id}`} onClick={() => { setSharing(h.id); setShareSaid(''); }}>Give out…</button>
                      )}
                      {renaming?.id !== h.id && <button type="button" className={btn} data-testid={`held-discard-${h.id}`} onClick={() => setDiscarding(h)}>Discard…</button>}
                    </div>
                    <p className="text-xs text-stone-300">{coinWords(h.coins) || 'No coins.'}</p>
                    {h.gems.length > 0 && <p className="text-xs text-stone-300">Gems: {h.gems.map((g) => (g.value === null ? g.name : `${g.name} (${money(g.value)})`)).join(', ')}</p>}
                    {h.items.length > 0 && list(h.items, '', `held-item-${h.id}`, h.id)}
                    {sharing === h.id && <HoardShare source={h} held={h} onDone={shared} />}
                  </div>
                ))}
              </div>
            )}
            {discarding && (
              <ConfirmDialog title={`Discard ${discarding.name}?`} confirmLabel="Discard it" destructive
                body={<p>Its coins, gems and items are not given to anyone.</p>}
                onConfirm={() => void discard(discarding)} onClose={() => setDiscarding(null)} />
            )}
          </div>
        )}
        {tab === 'shop' && (
          <div className="space-y-2">
            {kinds === null ? <p role="status" className="text-stone-400">Reading the site's shop kinds…</p> : (
              <div className="flex flex-wrap items-end gap-2">
                {kinds.kinds.length > 0 && (
                  <label className="text-xs text-stone-400">Kind
                    <select className={`${field} ml-1 w-auto`} value={kind} data-testid="shop-kind" onChange={(e) => setKind(e.target.value)}>
                      {kinds.kinds.map((k) => <option key={k.slug} value={k.slug}>{k.name}</option>)}
                    </select>
                  </label>
                )}
                {kinds.sizes.length > 0 && (
                  <label className="text-xs text-stone-400">Settlement
                    <select className={`${field} ml-1 w-auto`} value={size} data-testid="shop-size" onChange={(e) => setSize(e.target.value)}>
                      {kinds.sizes.map((k) => <option key={k.slug} value={k.slug}>{k.name}</option>)}
                    </select>
                  </label>
                )}
                <label className="text-xs text-stone-400">Budget
                  <input className={`${field} ml-1`} inputMode="decimal" value={shopBudget} placeholder={kinds.kinds.length ? 'the size\'s' : ''} data-testid="shop-budget" aria-label="Shop budget" onChange={(e) => setShopBudget(e.target.value)} />
                </label>
                <button type="button" className={btn} disabled={busy} data-testid="shop-stock" onClick={() => void stock()}>Stock the shop</button>
              </div>
            )}
            {wares && list(wares, 'The site found nothing to stock this shop with.', 'shop-item')}
          </div>
        )}
        {tab === 'saved' && (
          <div className="space-y-2">
            {open ? (
              <div data-testid="saved-shop">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-stone-100">{open.name}</p>
                  <button type="button" className={`${btn} ml-auto`} data-testid="saved-shop-back" onClick={() => { setOpen(null); fresh(); }}>All shops</button>
                </div>
                {open.notes && <p className="mt-1 whitespace-pre-wrap break-words text-xs text-stone-400">{open.notes}</p>}
                {list(open.wares, 'This shop has no wares.', 'saved-item')}
              </div>
            ) : saved === null ? <p role="status" className="text-stone-400">Reading your saved shops…</p>
              : saved.length === 0 ? <p className="text-stone-400" data-testid="saved-shops-none">You have no saved shops for this game system. Save one from the site's Shop Generator.</p>
                : (
                  <ul className="space-y-1" data-testid="saved-shops">
                    {saved.map((s) => (
                      <li key={s.id}>
                        <button type="button" className="w-full rounded border border-stone-700 px-2 py-1 text-left hover:bg-stone-800" data-testid={`saved-shop-${s.id}`} disabled={busy} onClick={() => void openShop(s.id)}>
                          <span className="text-stone-100">{s.name}</span><span className="text-stone-500">{' · '}{s.itemCount} {s.itemCount === 1 ? 'item' : 'items'}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
          </div>
        )}
      </div>
    </div>
  );
}

function Items({ rows, empty, testid, canGive, given, giving, onGive, giveList }: {
  rows: LootItem[]; empty: string; testid: string; canGive: boolean; given: Map<string, string[]>; giving: LootItem | null; onGive: (i: LootItem) => void; giveList: JSX.Element | null;
}) {
  if (!rows.length) return <p className="text-xs text-stone-400">{empty}</p>;
  // The row with the picker open; a re-read list brings new objects, so the same item is found by what it is (qa-runs 1197).
  const open = !giving ? -1 : rows.includes(giving) ? rows.indexOf(giving) : rows.findIndex((r) => itemKey(r) === itemKey(giving));
  return (
    <ul className="space-y-1">
      {rows.map((i, n) => {
        const to = given.get(itemKey(i));
        return (
          <li key={`${itemKey(i)}#${n}`} data-testid={`${testid}-${i.segment}-${i.slug}`} className={`rounded border px-2 py-1 ${to ? 'border-emerald-700' : 'border-stone-800'}`}>
            <div className="flex items-center gap-2">
              <span className="text-stone-100">{i.name}{i.quantity > 1 ? ` × ${i.quantity}` : ''}</span>
              {i.price !== null && <span className="text-xs text-stone-500">{money(i.price)}</span>}
              {to && <span className="text-xs text-emerald-300" data-testid="loot-given">Given to {to.join(', ')}</span>}
              {canGive && i.givable && (
                <button type="button" className={`${btn} ml-auto`} aria-label={`Give ${i.name} to a character`} data-testid={`loot-give-${i.segment}-${i.slug}`} onClick={() => onGive(i)}>Give to…</button>
              )}
              {canGive && !i.givable && i.made && <span className="ml-auto text-xs text-stone-500" title="Made up by the generator, not an entry of its own: write it on the sheet by hand.">not an entry</span>}
            </div>
            {n === open && giveList}
          </li>
        );
      })}
    </ul>
  );
}
